import { ChatMessageWithParts } from "src/views/assistant/types";
import { FileWithPreview } from "./fileTypes";

/**
 * Metadata for chat stream errors
 */
export interface ErrorMetadata {
  error_code?: string;
  requires_new_chat?: boolean;
  model_id?: number;
}

export interface ChatMessageWithFiles extends ChatMessageWithParts {
  files?: FileWithPreview[];
}

/**
 * Per-conversation answer style. "quick" asks the backend for a fast, direct
 * answer: nested-agent tools are withheld, Nuclearn Search is capped, and the
 * system prompt tells the model to be brief. "thorough" is the unchanged
 * default. Mirrors `mre.ops.chat.response_mode.RESPONSE_MODE_POLICIES`; a new
 * mode added here gets a composer button automatically.
 */
export const RESPONSE_MODES = ["thorough", "quick"] as const;
export type ResponseMode = (typeof RESPONSE_MODES)[number];
/** What the backend resolves an unsent or unknown mode to. */
export const DEFAULT_RESPONSE_MODE: ResponseMode = "thorough";
export const isResponseMode = (value: unknown): value is ResponseMode =>
  RESPONSE_MODES.includes(value as ResponseMode);

export type TextGenModelParams = {
  temperature?: number;
  top_p?: number;
  max_tokens?: number;
  enable_thinking?: boolean;
  end_token?: boolean;
};

export type ModelInferenceData = {
  infer_data: string; // will contain JSON stringified ChatMessage[]
  messages?: any[];
  dataset_list?: number[];
  stream?: boolean;
  streaming_response_required?: boolean;
  score_model_params?: Record<string, any>;
  text_gen_model_params?: TextGenModelParams;
};

export type EquipmentAIChatContext = {
  product_type: "equipment_ai";
  location_id?: string;
  af_element_id?: number;
  // Asset display name, persisted so a resumed asset chat shows the name +
  // breadcrumb leaf without re-fetching the AF ancestry.
  af_element_name?: string;
  // Alarm-analysis chat continuation: identifies which alarm/analysis the
  // resumed agent dialogue belongs to.
  alarm_definition_id?: string;
  alarm_analysis_id?: number;
  // System-scoped chat: the hierarchy node being viewed. The
  // backend resolves membership from the anchor mapping, so only the position
  // travels. Set only when neither af_element_id nor location_id is.
  // Mirrors mre.schema.chat_schema.EquipmentAIChatContext.
  system_value?: string;
  // Drill position above the system: a system value repeats across units, so
  // the unit/site the user drilled through narrow the scope; site/unit alone
  // (no system) scope a tier-level chat.
  site_value?: string;
  unit_value?: string;
};

/**
 * Snapshot of a product's chart-panel view at the moment of send.
 * Per-message: refreshed each turn so the model sees the user's most recent
 * state changes. Mirrors `mre.schema.chat_schema.ProductViewContext`.
 */
export type CustomSystemSensorRef = {
  tag: string;
  description?: string | null;
  uom?: string | null;
};

export type ProductViewContext = {
  start_time?: string; // ISO 8601
  end_time?: string; // ISO 8601
  time_bucket?: string; // e.g. "1 hour"
  aggregation?: string; // avg | max | min | sum | count
  visible_sensor_tags?: string[];
  hidden_sensor_tags?: string[];
  // Ad-hoc "custom system" membership (#21363) — a custom asset. Linked PI
  // points carry Tag/Description/Units (so the model gets the same per-point
  // context a real AF asset does); linked locations are the ERP filter ids.
  // Ephemeral — lives only on this chat's dialogue.config; presence of either
  // is the discriminator.
  custom_system_sensors?: CustomSystemSensorRef[];
  custom_system_location_ids?: string[];
};

/**
 * Filter predicate for RAG search, matching the backend FilterPredicate model.
 * Used to filter dataset records during chat search.
 */
export interface SearchFilterPredicate {
  filter_on: {
    group: string; // e.g., "dataset"
    name: string; // column name for filtering
    column: string; // same as name for datasets
  };
  comparison_operator: "eq" | "ne" | "lt" | "gt" | "le" | "ge" | "like";
  filter_value: string | number | boolean;
  value_datatype: "string" | "int" | "float" | "boolean" | "date";
}

/**
 * Dataset configuration with optional search filters.
 * When filters are provided, RAG search will only return records matching the filters.
 */
export interface DatasetWithFilters {
  id: number;
  name?: string;
  filters?: SearchFilterPredicate[];
}

export type NewConversation = {
  /** Model ID. If null, backend will use GeneralLLM from resource slot bindings */
  model_id: number | null;
  /** Model version number. If null, backend will resolve from resource slot bindings */
  model_version_number: number | null;
  model_inference_data: ModelInferenceData;
  dataset_id?: number | null;
  activity_id?: number | null;
  search_enhanced?: boolean;
  search_datasets?: number[];
  /** Source data record IDs to limit search to specific documents (intra-document search) */
  source_data_record_ids?: number[];
  n_search_results?: number;
  stream?: boolean;
  tool_calling?: boolean;
  chat_config_id?: number | null;
  product?: string | null;
  /** Product-specific context for specialized chat features (e.g., ER sensor plots) */
  product_context?: EquipmentAIChatContext;
  /** Snapshot of the chart-panel view at conversation start. Subsequent
   *  turns refresh this via the `view_context` field on the per-message
   *  ChatSendRequest body. */
  view_context?: ProductViewContext;
  /** Answer style for this conversation. Fixed by the first turn: the
   *  backend keeps the persisted value over anything a later start carries. */
  response_mode?: ResponseMode;
};

export type Conversation = NewConversation & {
  dialogue_id?: number;
};

export type ConversationRequest = Conversation | NewConversation;

export type Dialogue = {
  id: number;
  model_fk?: number | null;
  dataset_fk?: number | null;
  user_id?: number | null;
  name?: string | null;
  config?: DialogueConfig | null;
  messages?: string | null;
  /** ISO 8601 timestamp string, as returned by the API — never a real Date instance. */
  create_date: string;
  /** Opt-in link sharing: when true, any signed-in user with the link can view this chat read-only. */
  is_shared?: boolean;
  /** Parts AI dialogue kind ("run" | "comparison" | "search"); null/absent for non-Parts-AI dialogues. */
  kind?: string | null;
};

/**
 * Configuration for a chat instance
 * @typedef {Object} ChatConfig
 */
export type ChatConfig = {
  /** ID of the model to use for this chat. If null, backend will use GeneralLLM from resource slot bindings */
  modelId: number | null;
  /** Version number of the model. If null, backend will resolve from resource slot bindings */
  modelVersionNum: number | null;
  name?: string;
  slug?: string;
  description?: string;
  /** Optional prompt to start the conversation */
  initialPrompt?: string;
  /** Optional system prompt that guides the model's behavior */
  systemPrompt?: string;
  /** Optional array of datasets to include in context */
  datasets?: any[];
  /** Optional parameters to control text generation behavior */
  chatControlMetadata?: TextGenModelParams;
  /** Optional number of search results to return */
  topSearchResults?: number;
  /** Optional flag to enable tool calling capabilities */
  toolCalling?: boolean;
  /** Optional ID of the chat configuration in the database */
  chatConfigId?: number;
  /**
   * Model capabilities from inference_data_config.
   * Contains all capability flags for the model.
   */
  modelCapabilities?: ModelCapabilities;
};

/**
 * Model capability flags from inference_data_config
 */
export type ModelCapabilities = {
  /** Whether the model supports vision/image inputs */
  supportsVision?: boolean;
  /** Whether the model supports extended reasoning/thinking */
  supportsReasoning?: boolean;
  /** Whether the model supports function/tool calling */
  supportsFunctionCalling?: boolean;
};

export interface DialogueConfig {
  atom_assist_config_fk: number | null;
  dataset_ids: number[];
  temperature?: number;
  tool_calling: boolean;
  enable_thinking?: boolean;
  top_search_results?: number;
  is_agent?: boolean;
  /** Set when a project chat was opened via "Chat with document". */
  focused_file_name?: string | null;
  /** Thorough/Quick, fixed by the conversation's first turn. */
  response_mode?: ResponseMode;
}

export type ExtendedDialogueConfig = DialogueConfig & {
  datasets: Array<any>;
  persona: ChatConfig | null;
  slug?: string;
  name?: string;
  id?: number;
  description?: string;
  initial_prompt?: string;
  system_prompt?: string;
};
